import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { isEqual, pick } from "lodash";
import { useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import {
  createBillingDetails,
  getBillingDetails,
  purchasePlan,
  updateBillingDetails,
  verifyPlanPayment,
} from "../../../actionCreator/paymentActionCreator";
import { useAlert } from "../../../context/AlertProvider";
import {
  getAccountInfo,
  refreshToken,
} from "../../../actionCreator/commonActionCreator";

export const usePayment = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const billingDetails = useSelector(
    (state) => state.paymentReducer.billingDetails,
  );

  const handleBilling = async (values, callback, errorCallback) => {
    const payload = {
      ...pick(values, [
        "name",
        "companyName",
        "email",
        "mobileNumber",
        "hasGst",
        "gstNumber",
      ]),
      gstNumber: values?.hasGst
        ? values?.gstNumber
        : billingDetails?.content?.length
          ? ""
          : undefined,
      billingAddresses: [
        {
          ...pick(values, ["country", "address", "city", "state", "zipCode"]),
          type: "BILLING",
        },
      ],
    };

    const res = billingDetails?.content?.length
      ? !isEqual(
          {
            ...pick(billingDetails?.content[0], [
              "name",
              "companyName",
              "email",
              "mobileNumber",
              "hasGst",
              "gstNumber",
              "id",
            ]),
            billingAddresses: [
              {
                ...pick(billingDetails?.content[0]?.billingAddresses[0], [
                  "country",
                  "address",
                  "city",
                  "state",
                  "zipCode",
                ]),
                type: "BILLING",
              },
            ],
          },
          {
            ...payload,
            id: billingDetails?.content[0]?.id,
          },
        ) &&
        (await dispatch(
          updateBillingDetails({
            ...payload,
            id: billingDetails?.content[0]?.id,
          }),
        ))
      : await dispatch(createBillingDetails(payload));

    if (res?.response?.status) {
      errorCallback(res?.response?.data?.fieldErrors);
    } else {
      dispatch(getBillingDetails());
      searchParams.set("payment", "inprogress");
      navigate(`${location.pathname}?${searchParams.toString()}`);
      callback();
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const purchaseSubscription = async (payload, callback) => {
    setLoading(true);
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      alert("Failed to load Razorpay SDK");
      setLoading(false);
      return;
    }

    try {
      const res = await dispatch(purchasePlan(payload));
      const orderData = res?.data;
      if (res.status === 200) {
        const options = {
          key: orderData.key,
          amount: orderData.amount,
          currency: orderData.currency,
          name: "Your Company",
          description: `${payload?.packageName} Subscription`,
          order_id: orderData.orderId,
          handler: async (response) => {
            const verifyRes = await dispatch(
              verifyPlanPayment({
                razorpayOrderId: orderData.orderId,
                razorpayInvoiceId: response.razorpay_invoice_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                packageName: payload?.packageName,
                billingCycle: payload?.billingCycle,
              }),
            );
            await dispatch(refreshToken());
            await dispatch(getAccountInfo());
            callback();
            searchParams.set("payment", "success");
            navigate(`${location.pathname}?${searchParams.toString()}`);

            const verifyData = verifyRes;

            if (verifyData?.data?.invoiceUrl) {
              window.open(verifyData.data.invoiceUrl, "_blank");
            } else {
              showAlert("error", "Payment verification failed");
            }
          },
          theme: { color: "#3399cc" },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();

        setLoading(false);
      }
    } catch (err) {
      console.error("Error in payment flow:", err);
      showAlert("error", "Something went wrong, Payment failed.");
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getBillingDetails());
  }, [dispatch]);

  return { handleBilling, purchaseSubscription, loading, billingDetails };
};
